/**
 * JourneyMap — /journey overview page.
 *
 * Horizontal timeline of the 4 pillars showing where the visitor is,
 * what each pillar contains, and the next best action. Doubles as:
 *   - a returning-visitor landing surface ("resume where you left off")
 *   - an on-ramp / restart control (every station is a link)
 *   - the map that explains the site's mental model to first-timers
 */
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search, MapPin, ShieldCheck, GraduationCap, ArrowRight, Check, Cloud, Layers,
  Send,
} from "lucide-react";
import { useJourney } from "@/context/JourneyContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/app/SEOHead";
import JourneySaveHint from "@/components/JourneySaveHint";
import { StartHereGuide } from "@/components/journey/StartHereGuide";
import PersonaCard from "@/components/PersonaCard";
import { toast } from "sonner";
import { RealisationsLine } from "@/components/journey/RealisationsLine";
import { StageCelebration, useStageCelebrations } from "@/components/journey/StageCelebration";
import { JourneyComplete } from "@/components/journey/JourneyComplete";


const STATIONS = [
  {
    key: "pick",
    progress: "pick",
    label: "Pick",
    tagline: "What to study",
    icon: Search,
    colour: "#7C3AED",
    softBg: "bg-violet-50",
    softBorder: "border-violet-200",
    softText: "text-violet-800",
    hardBg: "bg-[#7C3AED]",
    blurb: "A 4-minute course-fit quiz, deep subject browsers, and the highest-earning-course lists — so your subject is a decision, not a guess.",
    features: [
      { label: "Course Fit quiz",     to: "/beta/course-fit",     description: "4 minutes · anonymous" },
      { label: "Highest-earning courses", to: "/highest-earning", description: "UK courses paying £45K+ three years out" },
      { label: "Browse every subject",   to: "/explore",           description: "22,000+ UCAS-verified codes" },
    ],
    primary: { to: "/beta/course-fit", label: "Take the Course Fit", short: "Course Fit" },
  },
  {
    key: "place",
    progress: "place",
    label: "Place",
    tagline: "City & university",
    icon: MapPin,
    colour: "#0891B2",
    softBg: "bg-cyan-50",
    softBorder: "border-cyan-200",
    softText: "text-cyan-800",
    hardBg: "bg-[#0891B2]",
    blurb: "1,000+ student cities scored on cost, climate, transport, safety and lifestyle. Pin 4 cities, then compare the universities inside them.",
    features: [
      { label: "City Matcher",     to: "/cities/matcher",  description: "13 lifestyle filters + climate + sectors" },
      { label: "Compare cities",   to: "/cities/compare",  description: "Radars across career / transport / lifestyle / climate" },
      { label: "University compare", to: "/compare",       description: "Side-by-side on outcomes + entry + cost" },
      { label: "Country guides",   to: "/apply",           description: "How applying works in each country" },
    ],
    primary: { to: "/cities/matcher", label: "Match a city", short: "Match a city" },
  },
  {
    key: "course",
    progress: "prove", // stored artefact: prove.chances_confirmed, written only by Quick Check
    label: "Course",
    tagline: "Can I get in?",
    icon: ShieldCheck,
    colour: "#002147",
    softBg: "bg-slate-50",
    softBorder: "border-slate-200",
    softText: "text-slate-800",
    hardBg: "bg-[#002147]",
    blurb: "See your admission odds against every university that offers your subject — Likely, Target, Ambitious — plus the grades and bursaries you actually qualify for.",
    features: [
      { label: "Admission Chances", to: "/admission",       description: "Likely / Target / Ambitious buckets" },
      { label: "Grade Calculator",  to: "/grade-calculator", description: "A*AA · IB 39 · SAT — instant translation" },
    ],
    primary: { to: "/admission", label: "Calculate my chances", short: "My chances" },
  },
  {
    key: "prove",
    progress: "advanced", // done only after a server-issued Advanced Search receipt is redeemed
    label: "Prove",
    tagline: "Assessment · where do I stand?",
    assessment: true,
    icon: Layers,
    colour: "#4C1D95",
    softBg: "bg-violet-50",
    softBorder: "border-violet-200",
    softText: "text-violet-900",
    hardBg: "bg-[#4C1D95]",
    blurb: "An assessment you can run any time between Course and Shortlist: Advanced Search scores courses on 6 layers across your full profile. Realisations feeds in here too.",
    features: [
      { label: "Advanced Search", to: "/advanced",     description: "6-layer matching engine" },
      { label: "My Realisations", to: "/realisations", description: "What your choices are telling you" },
    ],
    primary: { to: "/advanced", label: "Open Advanced Search", short: "Advanced Search" },
  },
  {
    key: "shortlist",
    progress: "apply", // stored artefact: how.shortlist_confirmed (confirming the shortlist)
    label: "Shortlist",
    tagline: "My final shortlist",
    icon: GraduationCap,
    colour: "#92400E",
    softBg: "bg-amber-50",
    softBorder: "border-amber-200",
    softText: "text-amber-800",
    hardBg: "bg-[#92400E]",
    blurb: "Lock in the 3–5 universities you'll actually apply to — with graduate salaries, employer pipelines and a parent-report you can share in one click.",
    features: [
      { label: "Compare universities",   to: "/compare",         description: "Side-by-side on outcomes + entry + cost" },
      { label: "Shortlist",              to: "/shortlist",        description: "Save up to 20 unis + notes + deadlines" },
      { label: "Parent report",          to: "/shortlist/parent-report", description: "One-click shareable summary" },
    ],
    primary: { to: "/shortlist", label: "Build my final list", short: "Final list" },
  },
  {
    key: "apply",
    progress: "applications", // done once every shortlisted application has been exported
    label: "Apply",
    tagline: "Make the applications",
    icon: Send,
    colour: "#B45309",
    softBg: "bg-orange-50",
    softBorder: "border-orange-200",
    softText: "text-orange-900",
    hardBg: "bg-[#B45309]",
    blurb: "Every course on your shortlist becomes an application — track its documents, deadlines and status in one place.",
    features: [
      { label: "Application tracker",  to: "/applications", description: "Documents, deadlines and status (sign in)" },
      { label: "Your shortlist",       to: "/shortlist",    description: "The courses each application comes from" },
      { label: "Parent report",        to: "/shortlist/parent-report", description: "Share your plan with family in one click" },
    ],
    primary: { to: "/applications", label: "Start applying", short: "Start applying" },
  },
];

// Segment to the next card: paints in with this stage's colour once the stage is complete.
function StageConnector({ station, next, done }) {
  if (!next) return null;
  const fill = { background: `linear-gradient(var(--dir), ${station.colour}, ${next.colour})` };
  return (
    <span aria-hidden data-testid={`journey-map-connector-${station.key}`} data-filled={done ? "true" : "false"}
      className="pointer-events-none absolute overflow-hidden rounded-full bg-stone-200 xl:-right-[14px] xl:top-[29px] xl:h-1.5 xl:w-4 max-xl:-bottom-[18px] max-xl:left-[21px] max-xl:h-5 max-xl:w-1.5">
      <motion.span className="absolute inset-0 origin-left max-xl:origin-top [--dir:to_bottom] xl:[--dir:to_right]" style={fill}
        initial={false} animate={{ scale: done ? 1 : 0 }} transition={{ duration: 0.6, ease: "easeOut" }} />
    </span>
  );
}

function StationCard({ station, state, index, onReset, next, celebrate }) {
  const Icon = station.icon;
  const done = state?.completed;
  const current = state?.current;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay: index * 0.08, duration: 0.4, ease: "easeOut" }}
      className={[
        "relative rounded-xl border-2 bg-white p-5 lg:p-6 flex flex-col gap-3 min-h-[340px]",
        current ? `border-2 shadow-md` : done ? "border-emerald-300" : "border-stone-200",
      ].join(" ")}
      style={current ? { borderColor: station.colour, boxShadow: `0 0 0 4px ${station.colour}12` } : {}}
      data-testid={`journey-map-card-${station.key}`}
    >
      <StageCelebration pending={celebrate} colour={station.colour} label={station.label} stageKey={station.key} />
      <StageConnector station={station} next={next} done={done} />
      {/* Number + icon on one row; the tagline gets its own fixed two-line slot so every title lines up. */}
      <div className="flex items-center gap-2">
        <span
          className={[
            "inline-flex items-center justify-center h-8 w-8 rounded-full font-mono text-sm font-semibold",
            done ? "bg-emerald-500 text-white" : current ? "text-white" : "bg-stone-100 text-stone-700",
          ].join(" ")}
          style={!done && current ? { backgroundColor: station.colour } : {}}
        >
          {done ? <Check className="h-4 w-4" /> : index + 1}
        </span>
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full" style={{ backgroundColor: `${station.colour}14` }}>
          <Icon className="h-4 w-4" style={{ color: station.colour }} aria-hidden />
        </span>
        {current && (
          <span className="ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white" style={{ backgroundColor: station.colour }}>
            You are here
          </span>
        )}
      </div>

      <div>
        <p className="font-mono text-[10px] font-medium uppercase leading-4 tracking-[0.14em] xl:min-h-[2rem]" style={{ color: station.colour }}
          data-testid={`journey-map-tagline-${station.key}`}>
          {station.tagline}
        </p>
        <h2 className="mt-1 font-editorial text-3xl leading-tight text-stone-900">
          {station.label}
        </h2>
      </div>

      <p className="text-sm text-stone-600 leading-relaxed">{station.blurb}</p>

      <ul className="mt-1 space-y-1.5 flex-1">
        {station.features.map((f) => (
          <li key={f.to} className="text-[13px]">
            <Link
              to={f.to}
              className="group flex items-start gap-1.5 hover:text-stone-900"
              data-testid={`journey-map-feature-${station.key}-${f.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <ArrowRight className="h-3 w-3 mt-1 text-stone-400 group-hover:translate-x-0.5 group-hover:text-stone-800 transition-all shrink-0" />
              <span className="flex-1">
                <span className="font-medium text-stone-800 group-hover:underline">{f.label}</span>
                <span className="block text-[11px] text-stone-500">{f.description}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>

      {/* One focusable link styled as a button: label left, arrow in its own disc on the right; same height on every card. */}
      <Link
        to={station.primary.to}
        data-testid={`journey-map-primary-${station.key}`}
        className={`group mt-2 flex w-full min-h-[52px] items-center justify-between gap-2 rounded-lg py-2 pl-4 pr-3 text-left text-[13px] font-semibold leading-tight text-white shadow-sm ${station.hardBg} transition-[transform,box-shadow,filter] duration-200 hover:-translate-y-0.5 hover:shadow-md hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2`}
      >
        <span className="min-w-0 break-words">
          {done && !current ? `Revisit ${station.label}` : (
            <><span className="xl:hidden">{station.primary.label}</span><span className="hidden xl:inline">{station.primary.short}</span></>
          )}
        </span>
        <ArrowRight className="h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden />
      </Link>

      {/* Row reserved on the 5-column layout so every CTA sits on the same line. */}
      <div className={done ? "lg:min-h-[24px]" : "hidden lg:block lg:min-h-[24px]"}>
        {done && (
          <button
            type="button"
            onClick={() => onReset?.(station.progress)}
            data-testid={`journey-map-reset-${station.key}`}
            className="text-[10px] leading-6 text-stone-400 hover:text-rose-600 underline underline-offset-2 self-start"
          >
            Reset this stage
          </button>
        )}
      </div>
    </motion.div>
  );
}

export default function JourneyMap() {
  const { pillarProgress, profile, loaded, resetPillar, resetJourney } = useJourney();
  const { user, login } = useAuth();
  const completed = STATIONS.filter((s) => pillarProgress?.[s.progress]?.completed).length;
  const celebrating = useStageCelebrations(STATIONS.filter((s) => pillarProgress?.[s.progress]?.completed).map((s) => s.key), loaded);
  const currentKey = pillarProgress?.currentPillar;
  const currentStation = STATIONS.find((s) => s.progress === currentKey);
  const nextStation = STATIONS.find((s) => !(pillarProgress?.[s.progress]?.completed)) || STATIONS[0];

  const handleReset = async (pillar) => {
    if (!window.confirm(
      `Reset this stage? Your saved data for ${pillar.toUpperCase()} will be cleared. You can rebuild it any time.`
    )) return;
    await resetPillar(pillar);
    toast.success(`${pillar[0].toUpperCase() + pillar.slice(1)} stage reset.`, {
      description: "The chip is grey again — head back into that tool to rebuild it.",
    });
  };
  const handleResetAll = async () => {
    if (!window.confirm(
      "Reset your entire journey? Every saved subject, city and grade will be cleared. This can't be undone (unless you're signed in)."
    )) return;
    await resetJourney();
    toast.success("Journey reset. Start anywhere.");
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2]" data-testid="journey-map-page">
      <SEOHead
        title="Your journey · Which University Advisor"
        description="Pick → Place → Course → Prove → Shortlist → Apply. Six steps from unsure to offer-in-hand — with progress tracking, restart points, and every tool in one place."
      />
      <JourneySaveHint />

      {/* Header */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 lg:pt-16 pb-6">
        <div className="label-uppercase text-stone-500">Your journey</div>
        <h1 className="mt-2 font-editorial text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-stone-900">
          From <span className="text-stone-500">"no idea"</span> to <em className="italic">offer in hand</em>.
        </h1>
        <p className="mt-6 text-base lg:text-lg text-stone-600 max-w-3xl leading-relaxed">
          Six practical steps. Pick your subject, place it in the right city and university,
          check your chances on the course, prove the shortlist is right for <em>you</em> with
          Advanced Search, lock in your final list, then export every application.
          Nothing here needs an account — sign in only if you want to sync across devices.
        </p>

        {/* Progress bar summary */}
        <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3" data-testid="journey-map-progress">
            <div className="w-40 h-2 rounded-full bg-stone-200 overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-[#7C3AED] to-[#0891B2]"
                initial={{ width: 0 }}
                animate={{ width: `${(completed / STATIONS.length) * 100}%` }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              />
            </div>
            <span className="text-sm font-medium text-stone-800 font-mono">
              {completed}/{STATIONS.length}
            </span>
            <span className="text-xs text-stone-500">stages completed</span>
          </div>

          {loaded && (
            <div className="flex-1 text-sm text-stone-700">
              {currentStation ? (
                <>
                  You're currently on <span className="font-semibold" style={{ color: currentStation.colour }}>{currentStation.label}</span>.
                  {" "}<Link to={currentStation.primary.to} className="underline hover:no-underline">Continue →</Link>
                </>
              ) : completed === 0 ? (
                <>
                  Start anywhere — most students begin with{" "}
                  <Link to="/beta/course-fit" className="underline hover:no-underline font-semibold text-[#7C3AED]">Course Fit</Link>{" "}
                  or the{" "}
                  <Link to="/cities/matcher" className="underline hover:no-underline font-semibold text-[#0891B2]">City Matcher</Link>.
                </>
              ) : completed === STATIONS.length ? (
                <>All six stages complete. Nice work — <Link to="/shortlist" className="underline hover:no-underline">review your shortlist →</Link></>
              ) : (
                <>Next up: <Link to={nextStation.primary.to} className="underline hover:no-underline font-semibold" style={{ color: nextStation.colour }}>{nextStation.label} · {nextStation.tagline}</Link></>
              )}
            </div>
          )}

          {!user && (
            <button
              type="button"
              onClick={login}
              data-testid="journey-map-signin"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[#002147] text-white hover:bg-[#001A38] shrink-0"
            >
              <Cloud className="h-3.5 w-3.5" />
              Sync my journey
            </button>
          )}
        </div>
      </section>

      {/* Persona card — first-person mirror of the current state */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-6">
        <PersonaCard variant="full" />
      </section>

      <StartHereGuide stations={STATIONS} pillarsCompleted={profile?.behaviour?.pillars_completed} loaded={loaded} />

      {/* Stations timeline — horizontal on desktop, stacked on mobile */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <JourneyComplete loaded={loaded} active={STATIONS.filter((s) => s.progress !== "applications").every((s) => pillarProgress?.[s.progress]?.completed)}
          applied={!!pillarProgress?.applications?.completed} />
        {/* Desktop timeline with connecting line */}
        <div className="hidden xl:block relative">
          <div className="grid grid-cols-6 gap-3 relative">
            {STATIONS.map((s, i) => (
              <StationCard
                key={s.key}
                station={s}
                state={pillarProgress?.[s.progress]}
                index={i}
                onReset={handleReset}
                next={STATIONS[i + 1]}
                celebrate={celebrating.has(s.key)}
              />
            ))}
          </div>
        </div>

        {/* Mobile: stacked cards with vertical connector */}
        <div className="xl:hidden space-y-4 relative">
          <div className="absolute left-6 top-4 bottom-4 w-0.5 bg-stone-200" aria-hidden />
          {STATIONS.map((s, i) => (
            <StationCard
              key={s.key}
              station={s}
              state={pillarProgress?.[s.progress]}
              index={i}
              onReset={handleReset}
              next={STATIONS[i + 1]}
              celebrate={celebrating.has(s.key)}
            />
          ))}
        </div>
      </section>

      <RealisationsLine />

      {/* Footer strip — how much of this needs sign-in */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <div className="rounded-xl bg-white border border-stone-200 p-6 lg:p-8 flex flex-col lg:flex-row items-start lg:items-center gap-4">
          <div className="flex-1">
            <div className="label-uppercase text-stone-500">Trust contract</div>
            <p className="mt-2 text-stone-800 font-editorial text-2xl leading-tight">
              Everything works without an account.
            </p>
            <p className="mt-2 text-sm text-stone-600 max-w-2xl">
              Your answers are remembered on this device. Sign in only when
              you want to sync between phone and laptop, get deadline reminders, or share
              your shortlist with a parent or counsellor.
            </p>
          </div>
          {!user && (
            <Button
              onClick={login}
              className="bg-[#002147] hover:bg-[#001A38] text-white rounded-md shrink-0"
              data-testid="journey-map-footer-signin"
            >
              <Cloud className="h-4 w-4 mr-1.5" /> Sign in
            </Button>
          )}
        </div>
        {completed > 0 && (
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={handleResetAll}
              data-testid="journey-map-reset-all"
              className="text-[11px] text-stone-400 hover:text-rose-600 underline underline-offset-2"
            >
              Reset everything and start over
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
